import { createFileRoute, Outlet, Link, useLocation } from "@tanstack/react-router";
import { AdminProtectedRoute } from "@/components/admin/admin-protected-route";
import { useAuth } from "@/hooks/use-auth";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { 
  LayoutDashboard, 
  Users, 
  Store, 
  Upload, 
  Tags, 
  AlertCircle, 
  ShieldCheck,
  BadgeCheck,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Menu,
  BarChart3,
  FileText,
  Settings,
  Mail as MailIcon
} from "lucide-react";
import { useState } from "react";
import logoAsset from "@/assets/logo.png.asset.json";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { ErrorBoundary } from "@/components/ui/error-boundary";

export const Route = createFileRoute("/admin")({
  component: AdminLayoutWrapper,
});

function AdminLayoutWrapper() {
  return (
    <AdminProtectedRoute>
      <AdminLayout />
    </AdminProtectedRoute>
  );
}

function AdminLayout() {
  const { profile, signOut } = useAuth();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const { data: counts } = useQuery({
    queryKey: ["admin-sidebar-counts"],
    queryFn: async () => {
      const [{ count: pendingClaims }, { count: newContacts }] = await Promise.all([
        supabase.from("reivindicacoes").select("*", { count: "exact", head: true }).eq("status", "pendente"),
        (supabase.from("contatos" as any) as any).select("*", { count: "exact", head: true }).eq("status", "novo"),
      ]);
      return { 
        claims: pendingClaims || 0,
        contacts: newContacts || 0 
      };
    },
  });

  const menuItems = [
    { title: "Visão Geral", icon: LayoutDashboard, href: "/admin", end: true },
    { title: "Usuários", icon: Users, href: "/admin/usuarios" },
    { title: "Fornecedores", icon: Store, href: "/admin/fornecedores" },
    { title: "Reivindicações", icon: BadgeCheck, href: "/admin/reivindicacoes", badge: counts?.claims },
    { title: "Importação", icon: Upload, href: "/admin/importar" },
    { title: "Segmentos", icon: Tags, href: "/admin/segmentos" },
    { title: "Relatórios", icon: BarChart3, href: "/admin/relatorios" },
    { title: "Denúncias", icon: AlertCircle, href: "/admin/denuncias" },
    { title: "Contatos", icon: MailIcon, href: "/admin/contatos", badge: counts?.contacts },
    { title: "Páginas", icon: FileText, href: "/admin/paginas" },
  ];

  if (profile && ["admin", "super_admin"].includes(profile.role)) {
    if (profile.role === "super_admin") {
      menuItems.push({ title: "Equipe", icon: ShieldCheck, href: "/admin/equipe" });
    }
    menuItems.push({ title: "Configurações", icon: Settings, href: "/admin/configuracoes" });
  }

  const SidebarContent = () => (
    <div className="flex flex-col h-full bg-[#0C1A12] text-white">
      <div className="p-6">
        <Link to="/" className="inline-block mb-4 shrink-0">
          <img src={logoAsset.url} alt="Logotipo Gopage" className="h-[60px] w-[60px] rounded-xl object-contain" />
        </Link>
        <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Admin Panel</p>
      </div>
      
      <ScrollArea className="flex-1 px-3">
        <div className="space-y-1">
          {menuItems.map((item) => {
            const isActive = item.end 
              ? location.pathname === item.href 
              : location.pathname.startsWith(item.href);
            
            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  "flex items-center gap-3 px-3 py-2 rounded-lg transition-colors group",
                  isActive 
                    ? "bg-primary text-[#0C1A12]" 
                    : "text-gray-400 hover:text-white hover:bg-white/5"
                )}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium flex-1">{item.title}</span>
                {item.badge !== undefined && item.badge > 0 && (
                  <Badge variant="destructive" className="ml-auto h-5 px-1.5 min-w-[20px] justify-center bg-[#C6F764] text-[#0C1A12] border-none">
                    {item.badge}
                  </Badge>
                )}
              </Link>
            );
          })}
        </div>
      </ScrollArea>

      <div className="p-4 mt-auto">
        <Separator className="bg-white/10 mb-4" />
        <div className="flex items-center gap-3 px-3 py-2 mb-2">
          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-[#0C1A12] font-bold text-xs">
            {profile?.nome?.[0] || "A"}
          </div>
          <div className="flex-1 overflow-hidden">
            <p className="text-sm font-medium truncate">{profile?.nome}</p>
            <p className="text-xs text-gray-400 truncate capitalize">{profile?.role?.replace("_", " ")}</p>
          </div>
        </div>
        <Button 
          variant="ghost" 
          className="w-full justify-start text-gray-400 hover:text-white hover:bg-white/5"
          onClick={() => signOut()}
        >
          <LogOut className="w-5 h-5 mr-3" />
          Sair
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-[#F7F9F5]">
      {/* Desktop Sidebar */}
      <aside className={cn(
        "hidden lg:flex flex-col border-r border-white/10 transition-all duration-300",
        isSidebarOpen ? "w-72" : "w-20"
      )}>
        <SidebarContent />
      </aside>

      {/* Mobile Bottom Nav */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 h-16 bg-[#0C1A12] border-t border-white/10 z-50 px-4 flex items-center justify-around">
        {menuItems.slice(0, 4).map((item) => {
          const isActive = item.end 
            ? location.pathname === item.href 
            : location.pathname.startsWith(item.href);
            
          return (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                "p-2 rounded-lg transition-colors",
                isActive ? "text-primary" : "text-gray-400"
              )}
            >
              <item.icon className="w-6 h-6" />
            </Link>
          );
        })}
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="text-gray-400">
              <Menu className="w-6 h-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 border-none bg-[#0C1A12] w-72">
            <SidebarContent />
          </SheetContent>
        </Sheet>
      </div>

      {/* Toggle Button (Desktop) */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className="hidden lg:flex fixed left-[272px] top-4 z-50 bg-[#0C1A12] text-white hover:bg-[#0C1A12]/90 rounded-full border border-white/10 shadow-lg w-8 h-8 transition-all duration-300"
        style={{ left: isSidebarOpen ? "272px" : "64px" }}
      >
        {isSidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </Button>

      <main className="flex-1 min-h-screen pb-20 lg:pb-0">
        <div className="p-4 md:p-8 max-w-7xl mx-auto">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </div>
      </main>
    </div>
  );
}
